import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { adUsersApi } from '../../../services/sponsoredAdsApi';
import SetAlert from '../../../components/SetAlert';

const AdUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [alertMessage, setAlertMessage] = useState({ success: '', error: '' });

  const [formData, setFormData] = useState({
    username: '',
    brandName: '',
    brandDetails: '',
    status: 'active'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adUsersApi.getAll();
      if (response.statusCode === "10000") {
        const transformedData = Array.isArray(response.data) 
          ? response.data.map(user => ({
              id: user.id,
              username: user.username,
              brandName: user.brand_name,
              brandDetails: user.brand_details,
              status: user.status || 'active'
            }))
          : [];
        setUsers(transformedData);
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch users' }));
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching users' }));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (user = null) => {
    if (user) {
      setFormData({
        username: user.username,
        brandName: user.brandName,
        brandDetails: user.brandDetails,
        status: user.status
      });
      setSelectedUser(user);
    } else {
      setFormData({
        username: '',
        brandName: '',
        brandDetails: '',
        status: 'active'
      });
      setSelectedUser(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({
      username: '',
      brandName: '',
      brandDetails: '',
      status: 'active'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiData = {
        username: formData.username,
        brand_name: formData.brandName,
        brand_details: formData.brandDetails,
        status: formData.status
      };
      
      const response = await (selectedUser 
        ? adUsersApi.update(selectedUser.id, apiData)
        : adUsersApi.create(apiData));

      if (response.statusCode === "10000") {
        setAlertMessage(prev => ({ 
          ...prev, 
          success: response.message || `User ${selectedUser ? 'updated' : 'created'} successfully` 
        }));
        handleCloseModal();
        fetchUsers();
      } else {
        setAlertMessage(prev => ({ 
          ...prev, 
          error: response.message || `Failed to ${selectedUser ? 'update' : 'create'} user` 
        }));
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setAlertMessage(prev => ({ 
        ...prev, 
        error: `Error ${selectedUser ? 'updating' : 'creating'} user` 
      }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await adUsersApi.delete(id);
        if (response.statusCode === "10000") {
          setAlertMessage(prev => ({ ...prev, success: response.message || 'User deleted successfully' }));
          fetchUsers();
        } else {
          setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to delete user' }));
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        setAlertMessage(prev => ({ ...prev, error: 'Error deleting user' }));
      }
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      inactive: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    const matchesSearch = (
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.brandName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesStatus;
  }) : [];

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ad-users-container">
      <SetAlert alertMessage={alertMessage} setAlertMessage={setAlertMessage} />
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Ad Users</h4>
          <Button variant="primary" onClick={() => handleShowModal()}>
            <FaPlus className="me-2" />
            New User
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
          </Row>

          <Table responsive hover>
            <thead>
              <tr>
                <th>Username</th>
                <th>Brand Name</th>
                <th>Brand Details</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.brandName}</td>
                  <td>{user.brandDetails}</td>
                  <td>{getStatusBadge(user.status)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleShowModal(user)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(user.id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedUser ? 'Edit User' : 'New User'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Brand Name</Form.Label>
              <Form.Control
                type="text"
                name="brandName"
                value={formData.brandName}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Brand Details</Form.Label>
              <Form.Control
                as="textarea"
                name="brandDetails"
                value={formData.brandDetails}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedUser ? 'Update' : 'Create'} User
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AdUsers; 